<template>
  <div class="base-table-wrap" :class="{ 'is-bordered': bordered }">
    <div v-if="title || $slots.toolbar" class="base-table-header">
      <h3 v-if="title" class="base-table-title">
        <span v-if="accent" class="base-table-accent" :style="accentStyle"></span>
        {{ title }}
        <span v-if="showCount && items.length" class="base-table-count">
          {{ items.length }}
        </span>
      </h3>
      <div v-if="$slots.toolbar" class="base-table-toolbar">
        <slot name="toolbar" />
      </div>
    </div>

    <div class="base-table-body">
      <div v-if="loading" class="base-table-state">
        <div class="base-table-skeleton">
          <div v-for="n in skeletonRows" :key="`sk-${n}`" class="skeleton-row"></div>
        </div>
      </div>

      <div v-else-if="!items.length" class="base-table-state base-table-empty">
        <slot name="empty">
          <div class="empty-glyph" aria-hidden="true">∅</div>
          <p>{{ emptyText }}</p>
        </slot>
      </div>

      <div
        v-else
        class="base-table-scroll"
        :class="{ 'is-sticky': stickyHeader }"
        :style="maxBodyHeight ? { maxHeight: maxBodyHeight, overflowY: 'auto' } : null"
      >
        <table class="base-table" :class="{ 'is-dense': dense, 'is-striped': striped, 'is-hover': hover, 'is-sticky-first-col': stickyFirstColumn }">
          <thead>
            <tr ref="headRow">
              <th
                v-for="(column, columnIndex) in resolvedColumns"
                :key="`th-${column.key}`"
                :class="[
                  { 'is-sortable': isColumnSortable(column) },
                  alignClass(column),
                  stickyColumnClass(columnIndex)
                ]"
                :style="columnStyle(column, columnIndex)"
                @click="isColumnSortable(column) && setSort(column.key)"
              >
                <slot :name="`header-${column.key}`" :column="column">
                  <span>{{ column.label }}</span>
                </slot>
                <span v-if="isColumnSortable(column)" class="sort-indicator">
                  {{ sortIndicator(column.key) }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, rowIndex) in sortedItems"
              :key="`tr-${rowIndex}`"
              :class="rowClass(row, rowIndex)"
              @click="$emit('row-click', row, rowIndex)"
            >
              <td
                v-for="(column, columnIndex) in resolvedColumns"
                :key="`td-${rowIndex}-${column.key}`"
                :class="[alignClass(column), stickyColumnClass(columnIndex)]"
                :style="stickyCellStyle(columnIndex)"
                :data-label="column.label"
              >
                <slot
                  :name="`cell-${column.key}`"
                  :value="row[column.key]"
                  :row="row"
                  :index="rowIndex"
                  :column="column"
                >
                  {{ formatCell(column, row[column.key]) }}
                </slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="paginated" class="base-table-footer">
      <span class="footer-info">
        {{ pageRangeLabel }}
      </span>
      <AppPager
        :current-page="currentPage"
        :last-page="totalPages"
        @change="changePage"
      />
    </div>
  </div>
</template>

<script>
const FORMATTERS = {
  currency: (value) => {
    if (value === null || value === undefined || value === '') return '—'
    const numeric = Number(value)
    if (Number.isNaN(numeric)) return value
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(numeric)
  },
  percent: (value) => {
    if (value === null || value === undefined || value === '') return '—'
    return `${value} %`
  },
  number: (value) => {
    if (value === null || value === undefined || value === '') return '—'
    const numeric = Number(value)
    if (Number.isNaN(numeric)) return value
    return new Intl.NumberFormat('en-IN').format(numeric)
  },
  date: (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${day}-${month}-${date.getFullYear()}`
  },
  datetime: (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString()
  }
}

import AppPager from './AppPager.vue'

export default {
  name: 'BaseTable',
  components: { AppPager },
  props: {
    items: { type: Array, default: () => [] },
    columns: { type: Array, default: null },
    title: { type: String, default: '' },
    accent: { type: String, default: '' },
    showCount: { type: Boolean, default: true },
    sortable: { type: Boolean, default: true },
    striped: { type: Boolean, default: true },
    hover: { type: Boolean, default: true },
    bordered: { type: Boolean, default: true },
    dense: { type: Boolean, default: false },
    stickyHeader: { type: Boolean, default: true },
    stickyFirstColumn: { type: Boolean, default: false },
    // Freeze the first N columns against horizontal scroll. Each frozen
    // column should declare an explicit `width` so the left offsets line
    // up; columns after the first frozen one only depend on the widths of
    // the columns ahead of them, so short id/code columns first is safe.
    stickyColumns: { type: Number, default: 0 },
    // When set (e.g. '65vh'), the scroll area gets a max-height and scrolls
    // internally so the sticky header actually engages on long pages.
    maxBodyHeight: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: 'No records to display.' },
    defaultSort: { type: Object, default: () => ({ column: '', direction: 'asc' }) },
    rowClassFn: { type: Function, default: null },
    skeletonRows: { type: Number, default: 5 },
    paginated: { type: Boolean, default: false },
    pageSize: { type: Number, default: 25 }
  },
  data () {
    return {
      sortState: {
        column: this.defaultSort?.column || '',
        direction: this.defaultSort?.direction || 'asc'
      },
      currentPage: 1,
      // Measured left offsets (px) for frozen columns, keyed by column
      // index. Measuring the real rendered widths avoids the sub-pixel
      // gap that appears between frozen columns when offsets are summed
      // from declared widths.
      measuredStickyLefts: {},
      measuredStickyRights: {}
    }
  },
  computed: {
    resolvedColumns () {
      if (Array.isArray(this.columns) && this.columns.length) {
        return this.columns.map((col) => this.normalizeColumn(col))
      }
      const first = this.items[0]
      if (!first || typeof first !== 'object') return []
      return Object.keys(first).map((key) => this.normalizeColumn(key))
    },
    accentStyle () {
      if (!this.accent) return null
      return { background: this.accent }
    },
    sortedItems () {
      const rows = Array.isArray(this.items) ? this.items : []
      const { column, direction } = this.sortState
      const sorted = column ? this.sortRows(rows, column, direction) : rows
      if (!this.paginated) return sorted
      const start = (this.currentPage - 1) * this.pageSize
      return sorted.slice(start, start + this.pageSize)
    },
    totalPages () {
      if (!this.paginated) return 1
      return Math.max(1, Math.ceil(this.items.length / this.pageSize))
    },
    pageRangeLabel () {
      if (!this.items.length) return '0 records'
      const start = (this.currentPage - 1) * this.pageSize + 1
      const end = Math.min(this.currentPage * this.pageSize, this.items.length)
      return `${start}–${end} of ${this.items.length}`
    }
  },
  watch: {
    items () {
      if (this.paginated && this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages
      }
    }
  },
  mounted () {
    this.$nextTick(this.recomputeStickyOffsets)
    window.addEventListener('resize', this.recomputeStickyOffsets)
  },
  updated () {
    // Re-measure after re-renders (data load, sort, pagination, column
    // changes) so the frozen-column offsets track the real layout.
    this.recomputeStickyOffsets()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.recomputeStickyOffsets)
  },
  methods: {
    normalizeColumn (col) {
      if (typeof col === 'string') {
        return {
          key: col,
          label: this.humanize(col),
          sortable: this.sortable,
          align: 'left',
          format: this.inferFormat(col)
        }
      }
      return {
        key: col.key,
        label: col.label != null ? col.label : this.humanize(col.key),
        sortable: col.sortable !== undefined ? col.sortable : this.sortable,
        align: col.align || 'left',
        width: col.width,
        sticky: col.sticky === true,
        stickyRight: col.stickyRight === true,
        format: col.format || this.inferFormat(col.key),
        formatter: col.formatter
      }
    },
    inferFormat (key) {
      const lower = String(key).toLowerCase()
      if (lower.includes('amount') || lower.includes('payment') || lower.includes('price')) return 'currency'
      if (lower.includes('rate') || lower.endsWith('_percent')) return 'percent'
      return null
    },
    humanize (key) {
      return String(key || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
    isColumnSortable (column) {
      return this.sortable && column.sortable !== false
    },
    alignClass (column) {
      if (column.align === 'right') return 'text-right'
      if (column.align === 'center') return 'text-center'
      return ''
    },
    isStickyColumn (index) {
      const col = this.resolvedColumns[index]
      if (col && col.sticky) return true
      // Back-compat: `stickyColumns` freezes the leading N columns.
      return this.stickyColumns > 0 && index < this.stickyColumns
    },
    isStickyRightColumn (index) {
      const col = this.resolvedColumns[index]
      return !!(col && col.stickyRight)
    },
    lastStickyIndex () {
      let last = -1
      for (let i = 0; i < this.resolvedColumns.length; i++) {
        if (this.isStickyColumn(i)) last = i
      }
      return last
    },
    firstStickyRightIndex () {
      for (let i = 0; i < this.resolvedColumns.length; i++) {
        if (this.isStickyRightColumn(i)) return i
      }
      return -1
    },
    stickyColumnClass (index) {
      if (this.isStickyColumn(index)) {
        return index === this.lastStickyIndex() ? 'bt-sticky-col bt-sticky-col--last' : 'bt-sticky-col'
      }
      if (this.isStickyRightColumn(index)) {
        return index === this.firstStickyRightIndex()
          ? 'bt-sticky-col-right bt-sticky-col-right--first'
          : 'bt-sticky-col-right'
      }
      return ''
    },
    parseWidth (width) {
      const n = parseInt(width, 10)
      return Number.isNaN(n) ? 0 : n
    },
    stickyLeft (index) {
      // Prefer the measured offset (exact rendered widths); fall back to
      // summing declared widths for the first paint before measurement.
      if (this.measuredStickyLefts[index] != null) {
        return this.measuredStickyLefts[index]
      }
      // Offset is relative to the first frozen column, so any leading
      // non-frozen columns (e.g. a serial-number column) scroll away
      // underneath the frozen ones instead of shifting their pinned edge.
      let left = 0
      for (let i = 0; i < index; i++) {
        if (this.isStickyColumn(i)) {
          left += this.parseWidth(this.resolvedColumns[i] && this.resolvedColumns[i].width)
        }
      }
      return left
    },
    recomputeStickyOffsets () {
      const hasLeft = this.lastStickyIndex() >= 0
      const hasRight = this.firstStickyRightIndex() >= 0
      if (!hasLeft && !hasRight) return
      const row = this.$refs.headRow
      if (!row || !row.children) return
      const cells = row.children

      // Left-frozen columns: cumulate widths from the left.
      const nextLeft = {}
      let cumulativeLeft = 0
      for (let i = 0; i < this.resolvedColumns.length; i++) {
        if (!this.isStickyColumn(i)) continue
        nextLeft[i] = cumulativeLeft
        const cell = cells[i]
        // Fractional width (not the integer offsetWidth) so the next
        // frozen column lands exactly on this one's edge — no gap,
        // no overlap.
        if (cell) cumulativeLeft += cell.getBoundingClientRect().width
      }

      // Right-frozen columns: cumulate widths from the right.
      const nextRight = {}
      let cumulativeRight = 0
      for (let i = this.resolvedColumns.length - 1; i >= 0; i--) {
        if (!this.isStickyRightColumn(i)) continue
        nextRight[i] = cumulativeRight
        const cell = cells[i]
        if (cell) cumulativeRight += cell.getBoundingClientRect().width
      }

      // Only commit when something actually changed (epsilon-compared to
      // ignore sub-pixel jitter), otherwise the updated() hook would loop.
      if (this.stickyOffsetsChanged(this.measuredStickyLefts, nextLeft)) {
        this.measuredStickyLefts = nextLeft
      }
      if (this.stickyOffsetsChanged(this.measuredStickyRights, nextRight)) {
        this.measuredStickyRights = nextRight
      }
    },
    stickyOffsetsChanged (prev, next) {
      const keys = Object.keys(next)
      const prevKeys = Object.keys(prev)
      return keys.length !== prevKeys.length ||
        keys.some((k) => Math.abs((prev[k] || 0) - next[k]) > 0.5)
    },
    stickyRightOffset (index) {
      if (this.measuredStickyRights[index] != null) {
        return this.measuredStickyRights[index]
      }
      // Offset is relative to the table's right edge — sum the declared
      // widths of the frozen-right columns that come after this one.
      let right = 0
      for (let i = index + 1; i < this.resolvedColumns.length; i++) {
        if (this.isStickyRightColumn(i)) {
          right += this.parseWidth(this.resolvedColumns[i] && this.resolvedColumns[i].width)
        }
      }
      return right
    },
    columnStyle (column, index) {
      const style = {}
      if (column.width) style.width = column.width
      if (this.isStickyColumn(index)) style.left = `${this.stickyLeft(index)}px`
      else if (this.isStickyRightColumn(index)) style.right = `${this.stickyRightOffset(index)}px`
      return Object.keys(style).length ? style : null
    },
    stickyCellStyle (index) {
      if (this.isStickyColumn(index)) return { left: `${this.stickyLeft(index)}px` }
      if (this.isStickyRightColumn(index)) return { right: `${this.stickyRightOffset(index)}px` }
      return null
    },
    setSort (key) {
      if (this.sortState.column === key) {
        this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortState.column = key
        this.sortState.direction = 'asc'
      }
    },
    sortIndicator (key) {
      if (this.sortState.column !== key) return '↕'
      return this.sortState.direction === 'asc' ? '↑' : '↓'
    },
    sortRows (rows, column, direction) {
      const factor = direction === 'asc' ? 1 : -1
      const copy = [...rows]
      copy.sort((a, b) => {
        const left = this.normalizeSortValue(a?.[column])
        const right = this.normalizeSortValue(b?.[column])
        if (left < right) return -1 * factor
        if (left > right) return 1 * factor
        return 0
      })
      return copy
    },
    normalizeSortValue (value) {
      if (value === null || value === undefined) return ''
      if (typeof value === 'number') return value
      if (typeof value === 'boolean') return value ? 1 : 0
      const asNumber = Number(value)
      if (!Number.isNaN(asNumber) && String(value).trim() !== '') return asNumber
      return String(value).toLowerCase()
    },
    formatCell (column, value) {
      if (typeof column.formatter === 'function') return column.formatter(value)
      if (column.format && FORMATTERS[column.format]) return FORMATTERS[column.format](value)
      if (value === null || value === undefined || value === '') return '—'
      return value
    },
    rowClass (row, index) {
      if (typeof this.rowClassFn === 'function') return this.rowClassFn(row, index)
      return ''
    },
    changePage (page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.$emit('page-change', page)
    }
  }
}
</script>

<style scoped>
.base-table-wrap {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.base-table-wrap.is-bordered {
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.base-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1.1rem;
  border-bottom: 1px solid #eef1f6;
}

.base-table-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.base-table-accent {
  display: inline-block;
  width: 4px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46, #16a07a);
}

.base-table-count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #475569;
  background: #f1f5f9;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
}

.base-table-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.base-table-body {
  flex: 1;
  min-height: 0;
}

.base-table-state {
  padding: 1.75rem 1.25rem;
}

.base-table-empty {
  text-align: center;
  color: #64748b;
}

.empty-glyph {
  font-size: 2rem;
  color: #cbd5e1;
  margin-bottom: 0.4rem;
}

.base-table-empty p {
  margin: 0;
  font-size: 0.9rem;
}

.base-table-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.skeleton-row {
  height: 30px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.base-table-scroll {
  overflow-x: auto;
  max-height: 100%;
}

.base-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.base-table thead th {
  background: #f8fafc;
  color: #475569;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.7rem 1rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  user-select: none;
}

.base-table-scroll.is-sticky thead th {
  position: sticky;
  top: 0;
  /* Above frozen body cells (z-index 2) so rows scroll under the header. */
  z-index: 3;
}

.base-table thead th.is-sortable {
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.base-table thead th.is-sortable:hover {
  background: #f0f9f4;
  color: #0e5b46;
}

.base-table thead th.is-sortable:hover .sort-indicator {
  color: #16a07a;
}

.sort-indicator {
  margin-left: 0.4rem;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
}

.base-table tbody td {
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #1f2937;
  vertical-align: middle;
  white-space: nowrap;
}

.base-table.is-dense thead th,
.base-table.is-dense tbody td {
  padding: 0.45rem 0.75rem;
}

.base-table.is-striped tbody tr:nth-child(even) td {
  background: #fbfcfe;
}

.base-table.is-hover tbody tr {
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.base-table.is-hover tbody tr:hover td {
  background: #f5fbf8;
}

.base-table.is-hover tbody tr:hover td:first-child {
  box-shadow: inset 3px 0 0 #16a07a;
}

.base-table tbody tr:last-child td {
  border-bottom: none;
}

.text-right { text-align: right; }
.text-center { text-align: center; }

.base-table.is-sticky-first-col thead th:first-child,
.base-table.is-sticky-first-col tbody td:first-child {
  position: sticky;
  left: 0;
  border-right: 1px solid #e5e9f2;
  box-shadow: 2px 0 4px -2px rgba(15, 23, 42, 0.08);
}

/* Corner cell: sticky on BOTH axes (top from the sticky-header rule, left
   from above). Needs a z-index above the other sticky headers (z-index 3) so
   they slide UNDER it on horizontal scroll instead of painting over it. */
.base-table.is-sticky-first-col thead th:first-child {
  background: #f8fafc;
  z-index: 4;
}

.base-table.is-sticky-first-col tbody td:first-child {
  background: #ffffff;
  z-index: 1;
}

.base-table.is-sticky-first-col.is-striped tbody tr:nth-child(even) td:first-child {
  background: #fbfcfe;
}

.base-table.is-sticky-first-col.is-hover tbody tr:hover td:first-child {
  background: #f5fbf8;
  box-shadow: inset 3px 0 0 #16a07a, 2px 0 4px -2px rgba(15, 23, 42, 0.08);
}

/* ── Frozen leading columns (stickyColumns prop) ── */
.base-table th.bt-sticky-col,
.base-table td.bt-sticky-col {
  position: sticky;
  z-index: 2;
}
.base-table td.bt-sticky-col {
  background: #ffffff;
}
.base-table thead th.bt-sticky-col {
  background: #f8fafc;
  /* Top-left corner: above both the sticky header (3) and frozen cells (2). */
  z-index: 5;
}
.base-table.is-striped tbody tr:nth-child(even) td.bt-sticky-col {
  background: #fbfcfe;
}
.base-table.is-hover tbody tr:hover td.bt-sticky-col {
  background: #f5fbf8;
}
.base-table th.bt-sticky-col--last,
.base-table td.bt-sticky-col--last {
  border-right: 1px solid #e5e9f2;
  box-shadow: 2px 0 4px -2px rgba(15, 23, 42, 0.08);
}

/* ── Frozen trailing columns (column.stickyRight) ── */
.base-table th.bt-sticky-col-right,
.base-table td.bt-sticky-col-right {
  position: sticky;
  z-index: 2;
}
.base-table td.bt-sticky-col-right {
  background: #ffffff;
}
.base-table thead th.bt-sticky-col-right {
  background: #f8fafc;
  z-index: 5;
}
.base-table.is-striped tbody tr:nth-child(even) td.bt-sticky-col-right {
  background: #fbfcfe;
}
.base-table.is-hover tbody tr:hover td.bt-sticky-col-right {
  background: #f5fbf8;
}
.base-table th.bt-sticky-col-right--first,
.base-table td.bt-sticky-col-right--first {
  border-left: 1px solid #e5e9f2;
  box-shadow: -2px 0 4px -2px rgba(15, 23, 42, 0.08);
}

.base-table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 1.1rem;
  border-top: 1px solid #eef1f6;
  background: #fafbfd;
  font-size: 0.8rem;
  color: #475569;
}

.pager {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.pager-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;
}

.pager-btn:hover:not(:disabled) {
  border-color: #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.pager-btn:focus-visible {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.pager-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pager-current {
  font-weight: 600;
}

/* ── Mobile card view ── */
@media (max-width: 640px) {
  .base-table-header {
    flex-wrap: wrap;
    padding: 0.7rem 0.85rem;
    gap: 0.5rem;
  }

  .base-table-title {
    font-size: 0.9rem;
  }

  .base-table-toolbar {
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .base-table-scroll {
    overflow-x: hidden;
    padding: 0.5rem;
  }

  .base-table {
    display: block;
    background: transparent;
    font-size: 0.85rem;
  }

  .base-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
    margin: -1px;
    padding: 0;
  }

  .base-table tbody {
    display: block;
  }

  .base-table tbody tr {
    display: block;
    background: #ffffff;
    border: 1px solid #e5e9f2;
    border-radius: 10px;
    padding: 0.5rem 0.65rem;
    margin-bottom: 0.55rem;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .base-table tbody tr:last-child {
    margin-bottom: 0;
  }

  .base-table.is-striped tbody tr:nth-child(even) td,
  .base-table.is-striped tbody tr:nth-child(even) {
    background: #ffffff;
  }

  .base-table.is-hover tbody tr:hover td,
  .base-table.is-hover tbody tr:hover td:first-child {
    background: transparent;
    box-shadow: none;
  }

  .base-table.is-hover tbody tr:hover {
    border-color: #16a07a;
    box-shadow: 0 2px 8px rgba(14, 91, 70, 0.10);
  }

  .base-table tbody td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.4rem 0;
    border-bottom: 1px dashed #eef1f7;
    white-space: normal;
    font-size: 0.85rem;
    word-break: break-word;
  }

  .base-table tbody td:last-child {
    border-bottom: none;
  }

  .base-table tbody td::before {
    content: attr(data-label);
    flex: 0 0 40%;
    max-width: 40%;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
    padding-right: 0.5rem;
  }

  .base-table tbody td.text-right,
  .base-table tbody td.text-center {
    text-align: left;
  }

  .base-table.is-sticky-first-col thead th:first-child,
  .base-table.is-sticky-first-col tbody td:first-child,
  .base-table th.bt-sticky-col,
  .base-table td.bt-sticky-col,
  .base-table th.bt-sticky-col-right,
  .base-table td.bt-sticky-col-right {
    position: static;
    box-shadow: none;
  }

  .base-table-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 0.45rem;
    padding: 0.65rem 0.85rem;
    text-align: center;
  }
}
</style>
